import { toast } from "react-toastify";
import { Cookies } from "react-cookie";

type LoginResponseData = {
  jwtToken?: string;
  userRole: string;
  userName?: string;
  id?: number | string;
};

type SessionCookieOptions = {
  path: string;
  sameSite: "lax";
  maxAge?: number;
  expires?: Date;
};

type SetCookieFn = (
  name: "LoveStrongToken" | "LoveStrongUser",
  value: string | LoginResponseData,
  options: SessionCookieOptions,
) => void;

/** Matches Better Auth session lifetime (100 hours). */
export const SESSION_MAX_AGE_SECONDS = 100 * 60 * 60;

export function getSessionCookieOptions(persist = true): SessionCookieOptions {
  const options: SessionCookieOptions = {
    path: "/",
    sameSite: "lax",
  };
  if (persist) {
    options.maxAge = SESSION_MAX_AGE_SECONDS;
    options.expires = new Date(Date.now() + SESSION_MAX_AGE_SECONDS * 1000);
  }
  return options;
}

let loggingOut = false;

export function beginLogout(): void {
  loggingOut = true;
}

export function isLoggingOut(): boolean {
  return loggingOut;
}

export function clearAuthProfileCookies(): void {
  const cookies = new Cookies();
  cookies.remove("LoveStrongToken", { path: "/" });
  cookies.remove("LoveStrongUser", { path: "/" });
}

export function isMissingAccessToken(message: unknown): boolean {
  const text = String(message || "").toLowerCase();
  return (
    text.includes("access token is not provided") ||
    text.includes("please not provided valid token") ||
    text.includes("session expired")
  );
}

export function applyLoginSession(
  response: { success?: boolean; data?: LoginResponseData; msg?: string },
  setCookie: SetCookieFn,
  options: {
    updatedUser?: (role: string) => void;
    navigate: (path: string, navOptions?: { replace?: boolean }) => void;
    redirectPath?: string | null;
    persist?: boolean;
    silent?: boolean;
  },
): void {
  if (!response.success || !response.data?.userRole) {
    return;
  }

  loggingOut = false;

  if (!options.silent) {
    const name =
      response.data.userName && response.data.userName !== ""
        ? response.data.userName
        : "";
    toast.success(name ? `Welcome ${name}` : "Welcome");
  }

  const cookieOptions = getSessionCookieOptions(options.persist !== false);
  const profile = { ...response.data };
  delete profile.jwtToken;
  setCookie("LoveStrongUser", profile, cookieOptions);
  const cookies = new Cookies();
  cookies.remove("LoveStrongToken", { path: "/" });
  options.updatedUser?.(response.data.userRole);
  if (!options.silent) {
    options.navigate(options.redirectPath || "/", { replace: true });
  }
}
